import { createContext, useContext } from 'react';
import type { ParticipantBreakdownData } from '@/services/api';
import type { ParticipantStatsHeaderProps } from '@/components/ParticipantStatsHeader';

// Shared state for the participant statistics layout
// (`/tournaments/:tournamentId/leaderboard/:userId/*`).
// One `useQuery` lives in the layout; tabs consume the resolved
// breakdown via this context instead of refetching.

export interface ParticipantStatsContextType {
  data: ParticipantBreakdownData;
  tournamentId: string;
  userId: string;
  displayName: string;
  // Accuracy-card buckets, computed once by the layout.
  distribution: ParticipantStatsHeaderProps['distribution'];
  // True when the layout is rendering `<ParticipantStatsHeader>` inside the
  // desktop sidebar. Tabs read this to avoid duplicating the same card inline
  // in the main column on desktop (the sidebar owns it). On mobile this is
  // false — sidebar is hidden by CSS, tab renders the header inline.
  headerInSidebar: boolean;
}

export const ParticipantStatsContext = createContext<ParticipantStatsContextType | undefined>(undefined);

export function useParticipantStatsContext() {
  const ctx = useContext(ParticipantStatsContext);
  if (!ctx) {
    throw new Error('useParticipantStatsContext must be used within ParticipantStatsLayout');
  }
  return ctx;
}
